<template>
  <div class="nodes">
    <template v-if="isShow">
      <div class="page-header">
        <h1>{{ $t('nodes.nodes') }}</h1>
        <Button class="top-create--btn" type="primary" icon="md-add" @click="showCreateModal">{{
          $t('nodes.addNode')
        }}</Button>
      </div>
      <Table :loading="loading" :columns="recordsCol" :data="records"></Table>
      <Page
        v-if="total >= 20"
        show-elevator
        show-total
        show-sizer
        :total="total"
        :page-size="limit"
        :page-size-opts="[20, 60, 80, 100]"
        @on-change="handlePageChange"
        @on-page-size-change="handlePageSizeChange"
      />
    </template>
    <EmptyPage
      v-else
      :text="$t('nodes.nullNodesText')"
      :btn-text="$t('nodes.addNode')"
      :img="require('../../assets/images/empty-icon.png')"
      @onClick="showCreateModal"
    >
    </EmptyPage>

    <Modal
      v-model="createModalVisible"
      class-name="nodes-modal--create"
      :title="isEdit ? $t('nodes.editNode') : $t('nodes.addNode')"
      :width="480"
      :mask-closable="false"
    >
      <Form ref="createForm" :model="createForm" :rules="rules">
        <Row>
          <i-col v-if="!isIEF" span="24">
            <FormItem :label="$t('nodes.nodeType')" prop="nodetype">
              <Select v-model="createForm.nodetype" size="large" @on-change="handleTypeChange">
                <Option v-for="item in typesList" :key="item.value" :value="item.value" :label="item.label"> </Option>
              </Select>
            </FormItem>
          </i-col>

          <i-col span="24">
            <FormItem :label="$t('nodes.nodeName')" prop="name">
              <i-input v-model="createForm.name" size="large"></i-input>
            </FormItem>
          </i-col>
          <i-col span="24">
            <FormItem :label="$t('nodes.endpoint')" prop="endpoint">
              <i-input v-model="createForm.endpoint" size="large" placeholder="http://127.0.0.1:9081"></i-input>
            </FormItem>
          </i-col>

          <template v-if="createForm.nodetype === IEF">
            <i-col span="24">
              <FormItem label="AK/SK Key" prop="gatewayKey">
                <i-input v-model="createForm.gatewayKey" size="large"></i-input>
              </FormItem>
            </i-col>
            <i-col span="24">
              <FormItem label="AK/SK Secret" prop="gatewaySecret">
                <Tooltip
                  v-if="isEdit"
                  max-width="250px"
                  transfer
                  :content="$t('nodes.secretEditRequired')"
                  placement="top"
                >
                  <Icon type="md-help-circle"></Icon>
                </Tooltip>
                <i-input v-model="createForm.gatewaySecret" size="large" type="password"></i-input>
              </FormItem>
            </i-col>
          </template>
        </Row>
      </Form>
      <div slot="footer">
        <Button type="text" @click="createModalVisible = false">{{ $t('common.cancel') }}</Button>
        <Button type="primary" @click="save">{{ $t('common.submit') }}</Button>
      </div>
    </Modal>

    <!-- import ruleset -->
    <ImportFileModel
      :modalVisible.sync="uploadRulesetModalVisible"
      :title="'nodes.importRuleset'"
      :isUploading="isImportingRuleset"
      @submit="importRulesets"
    />
  </div>
</template>

<script lang="jsx">
import { mapActions } from 'vuex'
import { loadNodes, createNode, deleteNode, viewNode, updateNode, exportRuleset, importRuleset } from '@/api/nodes'
import EmptyPage from '@/components/EmptyPage.vue'
import ImportFileModel from '@/components/ImportFileModel.vue'
import upload from '@/mixins/upload'

export default {
  name: 'Nodes',

  components: {
    EmptyPage,
    ImportFileModel,
  },

  mixins: [upload],

  data() {
    return {
      isShow: true,
      loading: false,
      isEdit: false,
      createModalVisible: false,
      IEF: 1,
      page: 1,
      limit: 20,
      total: 0,
      typesList: [
        {
          value: 0,
          label: this.$t('nodes.normalHttpRest'),
        },
        {
          value: 1,
          label: this.$t('nodes.huaweiIef'),
        },
      ],
      createForm: {},
      rules: {
        name: [{ required: true, message: this.$t('common.fillContent') }],
        nodetype: [{ required: true, message: this.$t('common.fillContent') }],
        endpoint: [{ required: true, message: this.$t('common.fillContent') }],
        gatewayKey: [{ required: true, message: this.$t('common.fillContent') }],
        gatewaySecret: [{ required: true, message: this.$t('common.fillContent') }],
      },
      records: [],
      uploadRulesetModalVisible: false,
      currentNodeRow: {},
      recordsCol: [
        {
          title: this.$t('nodes.nodeName'),
          key: 'name',
          maxWidth: 200,
          render: (h, params) => {
            return (
              <div>
                <a
                  href="javascript:;"
                  onClick={() => {
                    this.routeToNodesAction(params.row)
                  }}
                >
                  {params.row.name}
                </a>
              </div>
            )
          },
        },
        {
          title: this.$t('nodes.endpoint'),
          minWidth: 200,
          key: 'endpoint',
        },
        {
          title: this.$t('common.operations'),
          key: 'action',
          minWidth: 300,
          maxWidth: 400,
          align: 'center',
          render: (h, params) => {
            return (
              <div>
                <a
                  href="javascript:;"
                  style="margin-right: 10px;"
                  onClick={() => {
                    this.showDetail(params.row)
                  }}
                >
                  <span>{this.$t('common.edit')}</span>
                </a>
                <a
                  href="javascript:;"
                  style="margin-right: 10px;"
                  onClick={() => {
                    this.showUploadRulesetModal(params.row)
                  }}
                >
                  <span>{this.$t('nodes.importRuleset')}</span>
                </a>
                <a
                  href="javascript:;"
                  style="margin-right: 10px;"
                  onClick={() => {
                    this.exportRulesetByNodeID(params.row)
                  }}
                >
                  <span>{this.$t('nodes.exportRuleset')}</span>
                </a>
                <a href="javascript:;">
                  <Poptip
                    confirm={true}
                    transfer={true}
                    width={200}
                    title={this.$t('common.confirmDelete')}
                    placement="top"
                    type="error"
                    vOn:on-ok={() => {
                      this.remove(params.row)
                    }}
                    vOn:on-cancel={() => {}}
                  >
                    <span>{this.$t('common.delete')}</span>
                  </Poptip>
                </a>
              </div>
            )
          },
        },
      ],
      isImportingRuleset: false,
    }
  },

  computed: {
    currentNode() {
      return this.$store.state.currentNode
    },
    isIEF() {
      return this.$store.state.isIEF
    },
  },

  watch: {
    createModalVisible(val) {
      if (!val) {
        this.$refs.createForm.resetFields()
        this.isEdit = false
        this.createForm = {
          nodetype: this.isIEF ? 1 : undefined,
        }
      }
    },
  },

  created() {
    this.loadData()
    this.createForm = {
      nodetype: this.isIEF ? 1 : undefined,
    }
  },

  methods: {
    ...mapActions(['SET_NODES', 'SET_CURRENTNODE']),

    loadData() {
      this.loading = true
      loadNodes({
        $limit: this.limit,
        $skip: (this.page - 1) * this.limit,
      })
        .then((res) => {
          this.records = res.data.data
          this.total = res.data.total
          this.isShow = true
          if (!this.records.length) {
            this.isShow = false
          }
          this.loading = false
          const nodes = this.records.map((record) => ({
            label: record.name,
            value: record.id,
          }))
          this.SET_NODES(nodes)
        })
        .catch(() => {
          this.loading = false
        })
    },
    routeToNodesAction({ id, name }) {
      this.SET_CURRENTNODE({ id, name })
      this.$router.push({
        name: 'source',
        params: {
          id,
        },
      })
    },
    showDetail({ id }) {
      this.createModalVisible = true
      this.isEdit = true
      viewNode(id)
        .then((res) => {
          this.createForm = res.data
          this.handleTypeChange(this.createForm.nodetype)
        })
        .catch(() => {})
    },
    remove({ id }) {
      deleteNode(id)
        .then(() => {
          this.$Message.success(this.$t('common.deleteSuccess'))
          if (this.currentNode.id === id) {
            this.SET_CURRENTNODE({})
          }
          this.loadData()
        })
        .catch(() => {})
    },
    showCreateModal() {
      this.createModalVisible = true
    },
    save() {
      this.$refs.createForm.validate((valid) => {
        if (!valid) {
          return
        }
        if (this.isEdit) {
          const { id, nodetype, endpoint, gatewayKey, gatewaySecret, name } = this.createForm
          const data = {
            name,
            nodetype,
            endpoint,
            gatewayKey: nodetype === 0 ? '' : gatewayKey,
            gatewaySecret: nodetype === 0 ? '' : gatewaySecret,
          }
          updateNode(id, data)
            .then(() => {
              this.$Message.success(this.$t('common.editSuccess'))
              this.loadData()
              this.createModalVisible = false
            })
            .catch(() => {})
        } else {
          const data = { ...this.createForm }
          createNode(data)
            .then(() => {
              this.$Message.success(this.$t('common.addSuccess'))
              this.loadData()
              this.createModalVisible = false
            })
            .catch(() => {})
        }
      })
    },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    handlePageSizeChange(val) {
      this.limit = val
      this.page = 1
      this.loadData()
    },
    handleTypeChange(val) {
      if (val === 2) {
        this.loadgatewayOptions()
      }
    },
    routerToGateway() {
      this.$router.push({
        path: '/gateway',
      })
    },

    // export ruleset
    exportRulesetByNodeID(row) {
      exportRuleset(row.id).then((res) => {
        const { data, headers } = res
        const dataText = JSON.stringify(data, null, 2)
        this.downloadFile(headers, dataText, { fileName: `${row.name} ruleset` })
      })
    },

    // import ruleset
    showUploadRulesetModal(row) {
      this.uploadRulesetModalVisible = true
      this.currentNodeRow = row
    },
    closeUploadRulesetModal() {
      this.uploadRulesetModalVisible = false
      this.currentNodeRow = {}
    },
    importRulesets(data) {
      this.isImportingRuleset = true
      const nodeID = this.currentNodeRow.id
      importRuleset(nodeID, data)
        .then(() => {
          this.$Message.success(this.$t('common.uploadSuccess'))
          this.closeUploadRulesetModal()
        })
        .finally(() => {
          this.isImportingRuleset = false
        })
    },
  },
}
</script>

<style lang="scss">
.nodes-modal--create {
  .ivu-tooltip {
    position: absolute;
    left: 105px;
    top: 0px;
  }
  .ivu-icon-md-help-circle {
    font-size: 18px;
  }
}
</style>
